<template>
  <dashboard-tile class="mt-3" :loading="isLoading" loader-height="550px">
    <!-- Flex rather than a 8/4 grid split: the route filter is 250px wide on its own, so a
         fixed column would push the export menu onto a second line on narrower screens. -->
    <div class="d-flex align-items-center border-bottom w-100 m-0" data-testid="occupancy-header">
      <h6 class="dashboard-tile-title p-3 me-auto mb-0">{{ this.$t('flexigo.dashboard.occupancyRatios') }}</h6>
      <div class="p-2 d-flex align-items-center gap-2">
        <toggle-button-group v-if="shouldShowToggle" :options="tabOptions" @change="onTabChange" />
        <route-multiselect
          :routes="tabRoutes"
          :selected-route-ids="selectedRouteIds"
          :no-routes="noRoutes"
          @change="selectRoutes"
        />
        <!-- Same menu and the same four items as the ridership and OTP charts. Excel is the
             Shuttle Detailed Trips Report that used to sit here as its own download icon. -->
        <export-menu
          :formats="['png', 'pdf', 'print', 'excel']"
          :loading-format="isDownloading ? 'excel' : ''"
          @export="handleExport"
        />
      </div>
    </div>
    <div class="border-0">
      <!-- <highcharts :options="requestChartOptions"/> -->
      <div class="border-0" style="height: 50px"></div>

      <div class="bg-white p-3 outer-border">
        <div v-if="selectedTab === 'All'">
          <highcharts ref="allChart" :options="allChartOptions" />
        </div>
        <div v-if="selectedTab === 'Dynamic'">
          <highcharts ref="dynamicChart" :options="dynamicChartOptions" />
        </div>
        <div v-if="selectedTab === 'Fixed'">
          <highcharts ref="fixedChart" :options="fixedChartOptions" />
        </div>
      </div>
    </div>
  </dashboard-tile>
</template>
<script>
import Highcharts from 'highcharts';
import HighchartsExporting from 'highcharts/modules/exporting';
import HighchartsOfflineExporting from 'highcharts/modules/offline-exporting';
import { Chart } from 'highcharts-vue';
import { format } from 'date-fns';
import { enUS } from 'date-fns/locale';
import { getPercentage, formatPercent } from '@/helpers/utility';
import { intToDate } from '@/helpers/dateTime';
import { defaults } from '@/helpers/appDefaults';
import downloadRidershipReport from '@/modules/Flexigo/helpers/ridershipReport';
import DashboardTile from './DashboardTile.vue';
import RouteMultiselect from './RouteMultiselect.vue';
import toggleButtonGroup from '../toggleButton/toggleButtonGroup.vue';
import ExportMenu from './otp/components/ExportMenu.vue';
import { exportChartAsPNG, exportChartAsPDF, printChart } from './otp/utils/chartExport';
import { track } from '../../helpers/rum';
import toaster from '../../mixins/toaster';

// Highcharts is a singleton, so these also enable the export buttons the chart draws by
// itself — which this chart replaces with the menu in its header (`exporting.enabled: false`
// in occupancyChartOptions). Registering is idempotent; the modules guard against it.
HighchartsExporting(Highcharts);
HighchartsOfflineExporting(Highcharts);

let vm;

const RATIO_SERIES = [
  { index: 0, labelKey: 'flexigo.routeList.planned', color: '#5667ff' },
  { index: 1, labelKey: 'flexigo.dashboard.reportedByDriver', color: '#1eb64d' },
  { index: 2, labelKey: 'flexigo.dashboard.riderCheckins', color: '#f6b953' },
];

/**
 * Per-day series data for one working type. A day with no capacity had no active routes,
 * so all three ratios are undefined there: they become null — a gap in the line — instead
 * of the 0 the backend's defaults would draw (VSER-14215). `emptyDayAnchors` puts an
 * invisible point on exactly those days: Highcharts never fires a shared tooltip at an x
 * where every series is null, and the "No active routes on this day" message relies on it
 * (CombinedOtpChart uses the same trick).
 *
 * Days after today are only planned: nothing has been driven or checked into yet, so the
 * backend's 0 for the two measured ratios is not a real 0 and they are nulled as well
 * (VSER-14676). Today stays in — it is partially measured. Planned keeps running to the
 * end of the range.
 */
function toRatioSeries(rows = [], todayDay = Number(format(new Date(), 'yyyyMMdd'))) {
  const hasRoutes = (row) => (row?.capacity || 0) > 0;
  // A row past today has no measured values yet, only planned ones.
  const isMeasured = (row) => hasRoutes(row) && row.routeDay <= todayDay;
  return {
    planned: rows.map((row) => (hasRoutes(row) ? row.plannedRatio : null)),
    reportedByDriver: rows.map((row) => (isMeasured(row) ? getPercentage(row.reportedByDriver, row.capacity) : null)),
    riderCheckins: rows.map((row) => (isMeasured(row) ? row.riderCheckinsRatio : null)),
    emptyDayAnchors: rows.map((row) => (hasRoutes(row) ? null : 0)),
  };
}

/** Transparent, legend-less scatter series carrying the empty-day anchors (see above). */
function emptyDayAnchorSeries() {
  return {
    name: 'emptyDayAnchor',
    type: 'scatter',
    data: [],
    showInLegend: false,
    cursor: 'default',
    marker: {
      enabled: true,
      radius: 14,
      symbol: 'rect',
      fillColor: 'rgba(0,0,0,0)',
      lineWidth: 0,
      states: { hover: { enabled: false } },
    },
    // There is no day of routes to drill into — override the chart-wide click handler.
    point: { events: { click() {} } },
  };
}

/**
 * Tooltip body for both occupancy charts. Null points are left out of `points`, so each
 * ratio is looked up by series index rather than by position; a day where none of the ratio
 * series has a point had no active routes.
 */
function occupancyTooltip(points, category) {
  if (!points.length) return false;
  const header = `<div> ${category ?? points[0].key}</div>`;
  const chartSeries = points[0]?.series?.chart?.series || [];
  const ratioPoints = RATIO_SERIES.map(({ index }) => points.find((point) => point.series.index === index));

  if (ratioPoints.every((point) => !point)) {
    return `
                <div class="dashboard-graph-tooltip">
                  ${header}
                  <br/>
                  <div> No active routes on this day</div>
                </div>
            `;
  }

  const rows = RATIO_SERIES.map(({ index, labelKey }, i) => {
    // A series switched off in the legend has no point either; don't list it.
    if (chartSeries[index] && !chartSeries[index].visible) return '';
    const point = ratioPoints[i];
    const spacing = i < RATIO_SERIES.length - 1 ? ' mb-2 pb-1' : '';
    return `
                  <div class="d-flex justify-content-between${spacing}">
                    <span>${vm.$t(labelKey)}</span>
                    <span>${point ? formatPercent(point.y) : 'N/A'}</span>
                  </div>`;
  }).join('');

  return `
                <div class="dashboard-graph-tooltip">
                  ${header}
                  <br/>
                  <div> ${vm.$t('flexigo.dashboard.occupancyRatios')}</div>
                  <br/><br/>${rows}
                </div>
            `;
}

/**
 * Highcharts options for one tab. The All, Fixed and Dynamic tabs share them, so the All tab
 * also leaves gaps, nulls the measured ratios on future days, uses the same tooltip and only
 * exports through the header menu. `optionsKey` and `type` tell the click handler which tab's
 * day was clicked; prepareGraphData fills the categories and series data.
 */
function occupancyChartOptions(t, optionsKey, type) {
  return {
    chart: {
      type: 'spline',
    },
    // The header's export menu drives the exports; the chart's own button is off.
    exporting: {
      enabled: false,
    },
    credits: {
      enabled: false,
    },
    title: {
      text: null,
    },
    xAxis: {
      crosshair: true,
      lineWidth: 1,
      categories: [],
      gridLineWidth: 1,
    },
    yAxis: {
      allowDecimals: false,
      crosshair: true,
      lineWidth: 1,
      title: {
        // Default rotation (270) reads bottom-to-top along the axis.
        text: t('flexigo.dashboard.occupancyRatios'),
      },
    },
    legend: {
      itemStyle: {
        fontSize: '10px',
        color: '#7f8fa4',
      },
    },
    tooltip: {
      useHTML: true,
      formatter() {
        // Hovering the empty-day anchor lands here without `points`: it is a scatter
        // series, and scatter opts out of shared tooltips (`noSharedTooltip`), so
        // Highcharts hands over that one `point` instead of the day's array.
        return occupancyTooltip(this.points || (this.point ? [this.point] : []), this.x);
      },
      shared: true,
      backgroundColor: '#363647',
      style: {
        color: 'white',
      },
    },
    plotOptions: {
      series: {
        cursor: 'pointer',
        point: {
          events: {
            click(e) {
              vm.$data.selectedXaxisPoint = {
                date: e?.point?.category,
                chartOptionsType: optionsKey,
                type,
              };
            },
          },
        },
      },
    },
    series: [
      ...RATIO_SERIES.map(({ labelKey, color }) => ({
        name: t(labelKey),
        color,
        data: [],
        marker: {
          symbol: 'circle',
          lineWidth: 3,
          lineColor: color,
          fillColor: 'white',
        },
      })),
      emptyDayAnchorSeries(),
    ],
  };
}

export default {
  name: 'OccupancyGraph',
  components: { highcharts: Chart, DashboardTile, RouteMultiselect, ExportMenu, toggleButtonGroup },
  mixins: [toaster],
  props: {
    value: {
      type: Object,
      required: true,
    },
    isLoading: {
      type: Boolean,
      default: false,
    },
    dates: {
      type: Object,
      default: () => ({}),
    },
    // Routes the data on screen was fetched for; empty means every route. Owned by the dashboard,
    // which does the fetching (and goes back to every route when the tab changes).
    selectedRouteIds: {
      type: Array,
      default: () => [],
    },
    // The header's route groups have no routes: the filter says so, and the data is empty.
    noRoutes: {
      type: Boolean,
      default: false,
    },
    // The Excel export's route scope, when it differs from selectedRouteIds: the export endpoint takes
    // route ids only, so a dashboard narrowed by route groups passes their routes here. Empty exports every
    // route, null blocks the export (route groups picked, but their routes unknown). Unset: selectedRouteIds.
    exportRouteIds: {
      type: Array,
      default: undefined,
    },
  },
  data() {
    const t = (key) => this.$t(key);
    return {
      selectedXaxisPoint: undefined,
      selectedTab: null,
      isDownloading: false,
      shouldShowToggle: false,
      tabLabels: [
        {
          key: 'FIXED',
          labelKey: 'flexigo.workgroup.fixed',
        },
        {
          key: 'DYNAMIC',
          labelKey: 'flexigo.workgroup.dynamic',
        },
      ],
      dynamicChartOptions: occupancyChartOptions(t, 'dynamicChartOptions', 'DYNAMIC'),
      fixedChartOptions: occupancyChartOptions(t, 'fixedChartOptions', 'FIXED'),
      allChartOptions: occupancyChartOptions(t, 'allChartOptions', 'ALL'),
    };
  },
  computed: {
    locale() {
      return navigator.language;
    },
    hasFixedData() {
      return this.value?.FIXED?.data && this.value.FIXED.data.length > 0;
    },
    hasDynamicData() {
      return this.value?.DYNAMIC?.data && this.value.DYNAMIC.data.length > 0;
    },
    // The working types with a tab: those with data to show or routes to pick. The route filter lists every
    // route (VSER-15242), so a type the picked route groups have no data for still needs its tab, or its
    // routes couldn't be reached.
    availableTabs() {
      return [
        ['Fixed', this.hasFixedData || !!this.value?.FIXED?.routes?.length],
        ['Dynamic', this.hasDynamicData || !!this.value?.DYNAMIC?.routes?.length],
      ]
        .filter(([, available]) => available)
        .map(([tab]) => tab);
    },
    tabOptions() {
      return [
        {
          label: this.$t('flexigo.common.all'),
          selected: this.selectedTab === 'All',
        },
        {
          label: this.$t('flexigo.workgroup.fixed'),
          selected: this.selectedTab === 'Fixed',
        },
        {
          label: this.$t('flexigo.workgroup.dynamic'),
          selected: this.selectedTab === 'Dynamic',
        },
      ];
    },
    // The selected tab's routes, listed in the route filter.
    tabRoutes() {
      const dynamicRoutes = this.value?.DYNAMIC?.routes ?? [];
      const fixedRoutes = this.value?.FIXED?.routes ?? [];
      if (this.selectedTab === 'Dynamic') return dynamicRoutes;
      if (this.selectedTab === 'Fixed') return fixedRoutes;
      if (this.selectedTab === 'All') return [...dynamicRoutes, ...fixedRoutes];
      return [];
    },
  },
  watch: {
    availableTabs: {
      immediate: true,
      handler(tabs) {
        // Once shown, the toggle stays: picking a tab refetches that working type alone, and the other's
        // data goes with it.
        if (tabs.length === 2) this.shouldShowToggle = true;
        if (this.shouldShowToggle) {
          if (!this.selectedTab) this.selectedTab = 'All';
        } else if (tabs.length === 1) {
          [this.selectedTab] = tabs;
        }
      },
    },
    isLoading(value) {
      if (!value) this.prepareGraphData();
    },
    selectedXaxisPoint(value) {
      const dateIndex = this[value.chartOptionsType].xAxis.categories.findIndex((item) => value.date === item);

      let routeDay;
      if (value.type === 'ALL') {
        // For ALL type, use merged data
        const fixedData = this.value.FIXED.data;
        const dynamicData = this.value.DYNAMIC.data;
        const mergedData = this.mergeDataByDate(fixedData, dynamicData);
        routeDay = mergedData[dateIndex]?.routeDay;
      } else {
        // For FIXED or DYNAMIC, use specific data
        routeDay = this.value[value.type].data[dateIndex]?.routeDay;
      }

      if (!routeDay) return;

      const date = format(intToDate(routeDay), defaults.date);

      const queries = {
        ...this.$route.query,
        ...{
          selectedTab: 'routes',
          date: 'customDate',
          fromDate: date,
          toDate: date,
          workgroupType: 'SHUTTLE',
          workingTypes: value.type === 'ALL' ? undefined : value.type,
        },
      };
      const routeData = this.$router.resolve({ name: 'RouteStatuses', query: queries });
      window.open(routeData.href, '_blank');
    },
  },
  mounted() {
    this.prepareGraphData();
    vm = this;
  },
  methods: {
    getPercentage,
    formatPercent,
    onTabChange({ index }) {
      const tabs = ['All', 'Fixed', 'Dynamic'];
      this.selectedTab = tabs[index];
      this.$emit('tab-changed', this.selectedTab);
    },
    /**
     * The tab decides which chart is on screen, and only that one is ever exported.
     * `highcharts-vue` hands out the Highcharts instance as `chart`.
     */
    activeChart() {
      let chartComponent;
      if (this.selectedTab === 'All') {
        chartComponent = this.$refs.allChart;
      } else if (this.selectedTab === 'Dynamic') {
        chartComponent = this.$refs.dynamicChart;
      } else {
        chartComponent = this.$refs.fixedChart;
      }
      return chartComponent?.chart || null;
    },
    handleExport(exportFormat) {
      const filename = `occupancy-ratios-${this.selectedTab.toLowerCase()}`;
      if (exportFormat === 'png') {
        exportChartAsPNG(this.activeChart(), filename, { surface: 'occupancy' });
      } else if (exportFormat === 'pdf') {
        exportChartAsPDF(this.activeChart(), filename, 'occupancy');
      } else if (exportFormat === 'print') {
        printChart(this.activeChart(), 'occupancy');
      } else if (exportFormat === 'excel') {
        this.downloadExcel();
      }
    },
    async downloadExcel() {
      const routeScope = this.exportRouteIds === undefined ? this.selectedRouteIds : this.exportRouteIds;
      if (routeScope === null) {
        this.getToaster('warning', this.$t('flexigo.ridership.noRoutesInRouteGroups'));
        return;
      }
      track('export:excel:occupancy');
      try {
        this.isDownloading = true;
        // dates are dd.MM.yyyy, the ridership export expects YYYYMMDD integers
        const toDayInt = (day) => Number(day.split('.').reverse().join(''));
        await downloadRidershipReport({
          startDay: toDayInt(this.dates.startDay),
          endDay: toDayInt(this.dates.endDay),
          routeIds: routeScope,
        });
      } catch (error) {
        console.error(error);
      } finally {
        this.isDownloading = false;
      }
    },
    mergeDataByDate(fixedData, dynamicData) {
      // Create a map to merge data by routeDay
      const dataMap = new Map();

      // Add Fixed data - convert percentage ratios to counts
      fixedData.forEach((item) => {
        const key = item.routeDay;
        const capacity = item.capacity || 0;
        dataMap.set(key, {
          routeDay: item.routeDay,
          // Convert percentage to count: plannedRatio is 0-100, so divide by 100
          plannedCount: ((item.plannedRatio || 0) * capacity) / 100,
          reportedByDriver: item.reportedByDriver || 0,
          capacity,
          // Convert percentage to count
          riderCheckinsCount: ((item.riderCheckinsRatio || 0) * capacity) / 100,
        });
      });

      // Merge Dynamic data - add counts
      dynamicData.forEach((item) => {
        const key = item.routeDay;
        const itemCapacity = item.capacity || 0;

        if (dataMap.has(key)) {
          const existing = dataMap.get(key);
          dataMap.set(key, {
            routeDay: item.routeDay,
            // Add counts, not percentages
            plannedCount: existing.plannedCount + ((item.plannedRatio || 0) * itemCapacity) / 100,
            reportedByDriver: existing.reportedByDriver + (item.reportedByDriver || 0),
            capacity: existing.capacity + itemCapacity,
            riderCheckinsCount: existing.riderCheckinsCount + ((item.riderCheckinsRatio || 0) * itemCapacity) / 100,
          });
        } else {
          dataMap.set(key, {
            routeDay: item.routeDay,
            plannedCount: ((item.plannedRatio || 0) * itemCapacity) / 100,
            reportedByDriver: item.reportedByDriver || 0,
            capacity: itemCapacity,
            riderCheckinsCount: ((item.riderCheckinsRatio || 0) * itemCapacity) / 100,
          });
        }
      });

      // Convert counts back to percentages and return sorted array
      return Array.from(dataMap.values())
        .map((item) => ({
          routeDay: item.routeDay,
          // Convert count back to percentage (0-100 range)
          plannedRatio: item.capacity > 0 ? (item.plannedCount / item.capacity) * 100 : 0,
          reportedByDriver: item.reportedByDriver,
          capacity: item.capacity,
          riderCheckinsRatio: item.capacity > 0 ? (item.riderCheckinsCount / item.capacity) * 100 : 0,
        }))
        .sort((a, b) => a.routeDay - b.routeDay);
    },
    // No routes means every route.
    // An empty list means the user cleared the filter; the dashboard keeps it cleared.
    selectRoutes(routeIds) {
      this.$emit('occupancyRefresh', routeIds);
    },
    prepareGraphData() {
      if (this.value && this.value.DYNAMIC.data && this.value.FIXED.data) {
        const rowsByOptions = {
          dynamicChartOptions: this.value.DYNAMIC.data,
          fixedChartOptions: this.value.FIXED.data,
          // The All tab plots both working types summed per day.
          allChartOptions: this.mergeDataByDate(this.value.FIXED.data, this.value.DYNAMIC.data),
        };
        Object.entries(rowsByOptions).forEach(([optionsKey, rows]) => {
          const { planned, reportedByDriver, riderCheckins, emptyDayAnchors } = toRatioSeries(rows);
          this[optionsKey].xAxis.categories = rows.map((item) =>
            format(intToDate(item.routeDay), 'dd LLL', { locale: enUS }),
          );
          this[optionsKey].series[0].data = planned;
          this[optionsKey].series[1].data = reportedByDriver;
          this[optionsKey].series[2].data = riderCheckins;
          this[optionsKey].series[3].data = emptyDayAnchors;
        });
      }
    },
  },
};
</script>
